/**
 * Le jour civil à Madagascar — règles pures (décision 49 du 17/10).
 *
 * Le serveur tourne en UTC (le conteneur `api` n'a pas de `TZ`) : lire la
 * date avec `getDate()` ferait basculer la journée à 03:00 au comptoir. La
 * date civile se calcule donc ici, en heure de Madagascar, quel que soit le
 * fuseau du processus. Madagascar est à UTC+3 toute l'année, sans heure
 * d'été : un décalage fixe est exact et ne dépend pas des données de fuseau
 * du système.
 */
/** Le décalage de l'heure de Madagascar (`Indian/Antananarivo`) sur UTC. */
export const DECALAGE_MADAGASCAR_MINUTES = 180;
const MS_PAR_MINUTE = 60_000;
const MS_PAR_JOUR = 86_400_000;
/** La date civile `AAAA-MM-JJ` d'un instant, à Madagascar. */
export function jourAMadagascar(instant) {
    return new Date(instant.getTime() + DECALAGE_MADAGASCAR_MINUTES * MS_PAR_MINUTE)
        .toISOString()
        .slice(0, 10);
}
/**
 * Un jour civil `AAAA-MM-JJ` tel qu'une colonne `@db.Date` le stocke : minuit
 * UTC de ce jour. La seule conversion « jour du comptoir → date » (décision 372).
 */
export function jourEnDate(jour) {
    return new Date(`${jour}T00:00:00Z`);
}
/** L'année civile d'un instant, à Madagascar : le 1er janvier à 00:30, c'est déjà la nouvelle. */
export function anneeAMadagascar(instant) {
    return Number(jourAMadagascar(instant).slice(0, 4));
}
/**
 * Le dernier jour `AAAA-MM-JJ` du mois écoulé, à Madagascar : le 1er à 01:00,
 * heure de Tana, c'est déjà le mois écoulé d'hier qui est clos.
 */
export function finDuMoisPrecedentAMadagascar(instant) {
    const [annee, mois] = jourAMadagascar(instant).split('-').map(Number);
    return new Date(Date.UTC(annee, mois - 1, 0)).toISOString().slice(0, 10);
}
/** Les bornes `[debut, fin[` d'un jour civil `AAAA-MM-JJ` de Madagascar, en instants. */
export function bornesDuJourAMadagascar(jour) {
    const debut = new Date(Date.parse(`${jour}T00:00:00Z`) - DECALAGE_MADAGASCAR_MINUTES * MS_PAR_MINUTE);
    return { debut, fin: new Date(debut.getTime() + MS_PAR_JOUR) };
}
/**
 * Décision 458 — les bornes `[debut, fin[` d'un mois civil `AAAA-MM` de
 * Madagascar, en instants : du 1er à 00:00 de Tana au 1er du mois suivant.
 * Un passage du 31 à 23:30 de Tana (20:30 UTC) reste dans son mois.
 */
export function bornesDuMoisAMadagascar(mois) {
    const [annee, numero] = mois.split('-').map(Number);
    const suivant = new Date(Date.UTC(annee, numero, 1)).toISOString().slice(0, 10);
    return {
        debut: bornesDuJourAMadagascar(`${mois}-01`).debut,
        fin: bornesDuJourAMadagascar(suivant).debut,
    };
}
/**
 * CP-12 — une saisie notée sur papier pendant une coupure se ressaisit dans
 * la fenêtre réglée (en jours), jamais dans le futur. Une seule règle pour le
 * registre et la caisse : rend le motif du refus, ou `null`.
 */
export function refusDeRessaisie(survenueLe, maintenant, maxJours) {
    if (survenueLe.getTime() > maintenant.getTime())
        return 'FUTUR';
    if (maintenant.getTime() - survenueLe.getTime() > maxJours * MS_PAR_JOUR)
        return 'TROP_ANCIEN';
    return null;
}
/**
 * L'heure notée sur un papier, saisie `AAAA-MM-JJTHH:MM` (champ date et heure),
 * lue en heure de Madagascar quel que soit le fuseau du poste. `null` si la
 * saisie n'a pas cette forme.
 */
export function instantDeLHeureAMadagascar(saisie) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(saisie))
        return null;
    const instant = new Date(Date.parse(`${saisie}:00Z`) - DECALAGE_MADAGASCAR_MINUTES * MS_PAR_MINUTE);
    return Number.isNaN(instant.getTime()) ? null : instant;
}
/** L'inverse : un instant, en `AAAA-MM-JJTHH:MM` à l'heure de Madagascar. */
export function heureAMadagascar(instant) {
    return new Date(instant.getTime() + DECALAGE_MADAGASCAR_MINUTES * MS_PAR_MINUTE)
        .toISOString()
        .slice(0, 16);
}
//# sourceMappingURL=jour-civil.js.map